import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Save, RefreshCw, Lock, Unlock, Eraser, DownloadCloud } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/PageHeader';
import { ErrorAlert, SuccessAlert } from '../components/shared/AlertMessages';
import { Lightbox } from '../components/shared/Lightbox';
import { GeneralInfoSection } from '../components/intervention/GeneralInfoSection';
import { PersoneIdentificateSection } from '../components/intervention/PersoneIdentificateSection';
import { FormSectionsBlock } from '../components/intervention/FormSectionsBlock';
import { InformativaSection } from '../components/intervention/InformativaSection';
import { IDDocumentsSidebar } from '../components/intervention/IDDocumentsSidebar';
import { VVFStatusSidebar } from '../components/intervention/VVFStatusSidebar';
import { AudioFilesSidebar } from '../components/intervention/AudioFilesSidebar';
import { PhotoGallerySidebar } from '../components/intervention/PhotoGallerySidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_STATE = {
  numero_intervento: '', tipo_intervento: '', capo_partenza: '',
  richiedente: '', telefono: '', indirizzo: '', coordinate_maps: '',
  note: '', info_arrivo: '', provvedimenti: '', risultati_ottenuti: '',
  danni: '', persone_infortunate: '', causa_sinistro: '',
  provvedimenti_tutela: '', attivita_polizia: '', info_precedenti: '',
  inviata_informativa: false, persone_identificate: [], foto: [],
  audio_files: [], caricato_portale_vvf: false, data_caricamento_portale: null,
  manually_locked: false,
};

const InterventionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [intervention, setIntervention] = useState(INITIAL_STATE);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);

  const fetchIntervention = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await axios.get(`${API_URL}/api/interventions/${id}`, { withCredentials: true });
      setIntervention(res.data);
    } catch {
      setError("Errore nel caricamento dell'intervento");
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { fetchIntervention(); }, [fetchIntervention]);

  const handleChange = (field, value) => {
    setIntervention(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (isNew) {
        const res = await axios.post(`${API_URL}/api/interventions`, {
          numero_intervento: intervention.numero_intervento,
          tipo_intervento: intervention.tipo_intervento,
          capo_partenza: intervention.capo_partenza,
          richiedente: intervention.richiedente,
          telefono: intervention.telefono,
          indirizzo: intervention.indirizzo,
          coordinate_maps: intervention.coordinate_maps,
          note: intervention.note,
        }, { withCredentials: true });
        navigate(`/intervention/${res.data.id}`);
        setSuccess('Intervento creato con successo');
      } else {
        // Auto-lock on first manual save: protects against further AI overwrites
        // and webhook auto-additions until the user explicitly unlocks.
        await axios.put(`${API_URL}/api/interventions/${id}`, {
          info_arrivo: intervention.info_arrivo,
          provvedimenti: intervention.provvedimenti,
          risultati_ottenuti: intervention.risultati_ottenuti,
          danni: intervention.danni,
          persone_infortunate: intervention.persone_infortunate,
          causa_sinistro: intervention.causa_sinistro,
          provvedimenti_tutela: intervention.provvedimenti_tutela,
          attivita_polizia: intervention.attivita_polizia,
          info_precedenti: intervention.info_precedenti,
          inviata_informativa: intervention.inviata_informativa,
          persone_identificate: intervention.persone_identificate,
          manually_locked: true,
        }, { withCredentials: true });
        setIntervention(prev => ({ ...prev, manually_locked: true }));
        setSuccess('Intervento salvato e bloccato (le modifiche automatiche da Telegram/AI sono disattivate)');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLock = async () => {
    try {
      const res = await axios.post(
        `${API_URL}/api/interventions/${id}/lock`, {}, { withCredentials: true }
      );
      setIntervention(prev => ({ ...prev, manually_locked: res.data.manually_locked }));
      setSuccess(res.data.manually_locked
        ? 'Intervento bloccato: nessuna modifica automatica'
        : 'Intervento sbloccato: webhook e AI possono modificarlo');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore toggle lock');
    }
  };

  const handleResetForm = async () => {
    if (!window.confirm('Vuoi davvero azzerare tutta la compilazione del modulo? Le foto, gli audio e le persone identificate NON saranno toccati.')) return;
    try {
      await axios.post(`${API_URL}/api/interventions/${id}/reset-form`, {}, { withCredentials: true });
      setIntervention(prev => ({
        ...prev,
        info_arrivo: '', provvedimenti: '', risultati_ottenuti: '',
        danni: '', persone_infortunate: '', causa_sinistro: '',
        provvedimenti_tutela: '', attivita_polizia: '',
      }));
      setSuccess('Compilazione modulo azzerata');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore reset modulo');
    }
  };

  const handleSyncTelegram = async () => {
    const fromStr = window.prompt(
      'Sync da Telegram\n\nInserisci il primo message_id (incluso) — di solito il messaggio del primo media INVIATO dopo l\'intestazione di questo intervento.\n\nLo trovi cliccando "Copia link" su un messaggio: l\'ultimo numero dell\'URL è il message_id.'
    );
    if (!fromStr) return;
    const toStr = window.prompt('Inserisci l\'ULTIMO message_id del range (incluso). Di solito il messaggio dell\'ultimo media inviato per questo intervento (prima del prossimo numero di intervento).');
    if (!toStr) return;
    const fromId = parseInt(fromStr, 10);
    const toId = parseInt(toStr, 10);
    if (Number.isNaN(fromId) || Number.isNaN(toId)) {
      setError('Range non valido');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/interventions/${id}/sync-telegram`,
        { from_msg_id: fromId, to_msg_id: toId },
        { withCredentials: true }
      );
      setSuccess(res.data.message);
      await fetchIntervention();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore sync Telegram');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkUploaded = async () => {
    try {
      await axios.post(`${API_URL}/api/interventions/${id}/mark-uploaded`, {}, { withCredentials: true });
      setIntervention(prev => ({ ...prev, caricato_portale_vvf: true, data_caricamento_portale: new Date().toISOString() }));
      setSuccess('Intervento marcato come caricato sul portale VVF');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore');
    }
  };

  const addPersona = () => {
    setIntervention(prev => ({
      ...prev,
      persone_identificate: [...prev.persone_identificate, {
        cognome: '', nome: '', data_nascita: '', luogo_nascita: '',
        residenza: '', documento: '', in_qualita_di: '', foto_documento_url: null,
      }],
    }));
  };

  const updatePersona = (index, field, value) => {
    setIntervention(prev => {
      const updated = [...prev.persone_identificate];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, persone_identificate: updated };
    });
  };

  const removePersona = (index) => {
    if (!window.confirm('Sei sicuro di voler rimuovere questa persona?')) return;
    setIntervention(prev => ({
      ...prev,
      persone_identificate: prev.persone_identificate.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#E3000F] animate-spin" />
      </div>
    );
  }

  const headerTitle = isNew ? 'Nuovo Intervento' : `Intervento #${intervention.numero_intervento}`;
  const isLocked = !!intervention.manually_locked;
  const headerActions = (
    <div className="flex flex-wrap items-center gap-2">
      {!isNew && (
        <>
          <Button
            onClick={handleSyncTelegram}
            disabled={saving}
            variant="outline"
            className="border-[#333] bg-transparent text-zinc-200 hover:bg-[#1a1a1a] hover:text-white rounded-none uppercase tracking-wide text-xs"
            data-testid="sync-telegram-btn"
            title="Scarica i media da un range di message_id Telegram"
          >
            <DownloadCloud className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Sync Telegram</span>
            <span className="sm:hidden">Sync</span>
          </Button>
          <Button
            onClick={handleResetForm}
            disabled={saving}
            variant="outline"
            className="border-[#333] bg-transparent text-zinc-200 hover:bg-[#1a1a1a] hover:text-white rounded-none uppercase tracking-wide text-xs"
            data-testid="reset-form-btn"
            title="Azzera la compilazione AI delle 8 sezioni"
          >
            <Eraser className="w-4 h-4 mr-1.5" />
            <span className="hidden sm:inline">Azzera modulo</span>
            <span className="sm:hidden">Azzera</span>
          </Button>
          <Button
            onClick={handleToggleLock}
            disabled={saving}
            variant="outline"
            className={`rounded-none uppercase tracking-wide text-xs ${
              isLocked
                ? 'border-amber-500 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'
                : 'border-[#333] bg-transparent text-zinc-200 hover:bg-[#1a1a1a] hover:text-white'
            }`}
            data-testid="lock-toggle-btn"
            title={isLocked ? 'Sblocca per consentire modifiche automatiche' : 'Blocca contro modifiche automatiche'}
          >
            {isLocked ? <Lock className="w-4 h-4 mr-1.5" /> : <Unlock className="w-4 h-4 mr-1.5" />}
            <span className="hidden sm:inline">{isLocked ? 'Bloccato' : 'Modificabile'}</span>
            <span className="sm:hidden">{isLocked ? 'Locked' : 'Open'}</span>
          </Button>
        </>
      )}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="bg-[#E3000F] hover:bg-[#CC000D] text-white rounded-none uppercase tracking-wide"
        data-testid="save-intervention-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Salvataggio...' : 'Salva'}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505]">
      <PageHeader title={headerTitle} actions={headerActions} />

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <ErrorAlert message={error} onClose={() => setError('')} />
        <SuccessAlert message={success} onClose={() => setSuccess('')} />

        {!isNew && isLocked && (
          <div
            className="mb-4 border border-amber-500/40 bg-amber-500/5 px-4 py-2 text-sm text-amber-300 flex items-center gap-2"
            data-testid="lock-banner"
          >
            <Lock className="w-4 h-4 flex-shrink-0" />
            <span>
              Intervento <strong>bloccato</strong>: le modifiche automatiche da Telegram e dall'AI sono disattivate.
              Sblocca per riattivarle.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <GeneralInfoSection intervention={intervention} isNew={isNew} onChange={handleChange} />
            {!isNew && (
              <PersoneIdentificateSection
                persone={intervention.persone_identificate}
                onAdd={addPersona}
                onUpdate={updatePersona}
                onRemove={removePersona}
              />
            )}
            {!isNew && <FormSectionsBlock intervention={intervention} onChange={handleChange} />}
            {!isNew && <InformativaSection value={intervention.inviata_informativa} onChange={(v) => handleChange('inviata_informativa', v)} />}
          </div>

          {/* Sidebar */}
          {!isNew && (
            <div className="space-y-6">
              <IDDocumentsSidebar persone={intervention.persone_identificate} interventionId={intervention.id} onImageClick={setLightboxImage} onRefresh={fetchIntervention} />
              <VVFStatusSidebar caricato={intervention.caricato_portale_vvf} dataCaricamento={intervention.data_caricamento_portale} onMarkUploaded={handleMarkUploaded} />
              <AudioFilesSidebar audioFiles={intervention.audio_files} interventionId={intervention.id} onRefresh={fetchIntervention} />
              <PhotoGallerySidebar interventionId={intervention.id} photos={intervention.foto} onImageClick={setLightboxImage} onRefresh={fetchIntervention} />
            </div>
          )}
        </div>
      </main>

      <Lightbox imageUrl={lightboxImage} onClose={() => setLightboxImage(null)} />
    </div>
  );
};

export default InterventionDetailPage;
